import { adminPath, useScope } from "../lib/scope";
import { rp } from "../api/client";
import {
  AlertIcon, CheckCircleIcon, ClockIcon, CpuIcon, GitPullRequestIcon, HistoryIcon, KeyIcon, 
  PlusIcon, RepoIcon, 
} from "@primer/octicons-react";
import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAction, useOrgAudit, useQueue, useRepos } from "../api/hooks";
import { type QueueStats, type TokenClaims } from "../api/types";
import { CopyButton, CopyField, DescriptionList, Empty, ErrorMessage, LiveDot, Pre, Query, Sha, TimeAgo, Who } from "../components/common";
import { safeJson, timeAgo } from "../components/format";
import { Subhead } from "../components/SettingsLayout";
import { Alert, AlertTitle } from "../components/ui/alert";
import { Badge, Counter } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader } from "../components/ui/card";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import { Field, FieldGrid } from "../components/ui/field";
import { Input } from "../components/ui/input";
import { Select } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Chip, ChipGroup } from "../components/ui/toggle-group";
import { Tooltip } from "../components/ui/tooltip";
import { cn } from "../lib/utils";

/** Mirrors the server's subRe (auth/tokens.go). */
const SUBJECT_RE = /^[A-Za-z0-9._@-]{1,64}$/;

/** Queued jobs no runner has picked up for this long are flagged on the overview. */
const STALL_MS = 120_000;
const ON_DEMAND_STALL_MS = 300_000;

const waiting = (q: QueueStats) => q.jobs.reduce((n, j) => n + (j.state === "queued" ? j.n : 0), 0);

/** What each job kind does and which process claims it (jobs/jobs.go); remote sync is run by the platform. */
const JOB_KINDS: Record<string, { label: string; worker: string; token: string; start: string; does: string }> = {
  ci: { label: "CI", worker: "bee-runner", token: "CI runner", start: "make runner", does: "runs a repository's checks on a commit" },
  remote: { label: "Remote", worker: "bee-bridge", token: "", start: "", does: "pulls the GitHub branch into main, pushes the evidence notes, and opens or merges the batch PR" },
  import: { label: "Import", worker: "bee-bridge", token: "", start: "", does: "clones a GitHub repository into a new, empty repository" },
};
const kindInfo = (k: string) => JOB_KINDS[k] ?? { label: k, worker: "a worker", token: "", start: "", does: k };

/** The platform runs remote sync itself, so users see what it does rather than the process name. */
const workerName = (w: string) => (w === "bee-bridge" ? "remote sync" : w);

const since = (ms: number) => timeAgo(ms).replace(" ago", "");

/** "ssh://git@host:2222/a/b.git", "git@host:a/b.git" and "https://host/a/b" all read as "host/a/b". */
const importSource = (url?: string) => url?.match(/^(?:[a-z+]+:\/\/)?(?:[^@/]+@)?([^/:]+)(?::\d+)?[:/](.+?)(?:\.git)?\/?$/i)?.slice(1).join("/") ?? url ?? "";

/** Workers with jobs waiting longer than STALL_MS while none of their jobs is running. */
function stalledWorkers(q: QueueStats) {
  const busy = new Set(q.claimed.map((j) => kindInfo(j.kind).worker));
  const onDemand = new Set(q.on_demand);
  const out = new Map<string, { start: string; token: string; n: number; oldest: number; onDemand: boolean }>();
  for (const j of q.queued) {
    const k = kindInfo(j.kind);
    // A worker the server starts itself is given longer: a Cloud Run Job takes a while to cold start.
    if (busy.has(k.worker) || Date.now() - j.created <= (onDemand.has(k.worker) ? ON_DEMAND_STALL_MS : STALL_MS)) continue;
    const s = out.get(k.worker) ?? { start: k.start, token: k.token, n: 0, oldest: j.created, onDemand: onDemand.has(k.worker) };
    s.n++;
    s.oldest = Math.min(s.oldest, j.created);
    out.set(k.worker, s);
  }
  return [...out].map(([worker, s]) => ({ worker, ...s }));
}

/* ---------- tokens ---------- */

/** An organization issues agent tokens, scoped to its own repositories; the operator may issue the other kinds too. */
const TTLS = [1, 4, 8, 24, 24 * 7, 24 * 30, 24 * 90, 24 * 365];
const DEFAULT_TTL = 8;
const KINDS = {
  agent: ["read", "write"],
  admin: ["read", "write", "admin", "ci", "bridge"],
  runner: ["read", "ci"],
  bridge: ["read", "bridge"],
} as const;
type Kind = keyof typeof KINDS;

const hours = (h: number) => {
  if (h < 24) return `${h} hour${h === 1 ? "" : "s"}`;
  const d = h / 24;
  return d === 365 ? "1 year" : `${d} day${d === 1 ? "" : "s"}`;
};
const csv = (s: string) => s.split(",").map((x) => x.trim()).filter(Boolean);
const scope = (v: string[], all: string, label: string) => (!v.length || v.includes(all) ? `all ${label}` : v.join(", "));

export function AdminTokens() {
  const [params, setParams] = useSearchParams();
  const open = params.has("new");
  const setOpen = (o: boolean) => setParams(o ? { new: "" } : {}, { replace: true });
  return (
    <>
      <Subhead title="Tokens">
        Tokens are signed and scoped to the repositories and paths you list. The server keeps no copy and cannot revoke one, so prefer short lifetimes and treat long-lived ones like passwords.
      </Subhead>
      <RecentTokens onNew={() => setOpen(true)} />
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="top-[6vh] max-h-[88vh] w-[min(720px,calc(100vw-32px))] overflow-y-auto">
          {open && <NewToken onDone={() => setOpen(false)} />}
        </DialogContent>
      </Dialog>
    </>
  );
}

function NewToken({ onDone }: { onDone: () => void }) {
  const org = useScope();
  const act = useAction();
  const repos = useRepos();
  const [f, setF] = useState({ sub: "", task: "", paths: "", ttl: DEFAULT_TTL, kind: "agent" as Kind });
  // Empty selection means every repository the issuer can reach.
  const [picked, setPicked] = useState<string[]>([]);
  const set = (k: "sub" | "task" | "paths") => (e: React.ChangeEvent<HTMLInputElement>) => setF({ ...f, [k]: e.target.value });
  const subBad = f.sub !== "" && !SUBJECT_RE.test(f.sub);
  const issued = act.data as { token: string; claims: TokenClaims } | undefined;
  const names = (repos.data ?? []).map((r) => r.name);
  const repoScope = picked;
  const pathScope = csv(f.paths);
  const incomplete = !f.sub || subBad;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (incomplete) return;
    act.mutate({
      path: adminPath(org, "/tokens"),
      body: { sub: f.sub, kind: f.kind, task: f.task || undefined, repos: repoScope, paths: pathScope, ttl_s: f.ttl * 3600 },
    });
  };

  if (issued)
    return (
      <div className="px-4 py-4">
        <IssuedToken token={issued.token} claims={issued.claims} onDone={onDone} />
      </div>
    );

  return (
    <form onSubmit={submit}>
      <div className="border-b border-border-muted px-4 py-3">
        <DialogTitle className="flex items-center gap-2 text-sm font-semibold">
          <KeyIcon /> New token
        </DialogTitle>
      </div>
      <CardContent>
        <FieldGrid>
          <Field label="Name" required invalid={subBad} hint={subBad ? "1–64 letters, digits, '.', '_', '@' or '-'." : "Who uses it; shown in the audit log."}>
            <Input autoFocus value={f.sub} onChange={set("sub")} placeholder="my-agent" aria-invalid={subBad} required />
          </Field>
          {!org && (
            <Field label="Kind" hint="Admin tokens can do anything; runner tokens run CI jobs.">
              <Select value={f.kind} onValueChange={(v) => setF({ ...f, kind: v as Kind })} options={(Object.keys(KINDS) as Kind[]).map((k) => ({ value: k, label: k }))} />
            </Field>
          )}
          <Field label="Expires after">
            <Select value={String(f.ttl)} onValueChange={(v) => setF({ ...f, ttl: Number(v) })} options={TTLS.map((h) => ({ value: String(h), label: hours(h) }))} />
          </Field>
        </FieldGrid>
        <details className="text-sm">
          <summary className="cursor-pointer text-muted-foreground">Limit access (optional)</summary>
          <div className="mt-3 flex flex-col gap-4">
            <Field label="Repositories" hint="None selected means all.">
              <div className="flex flex-wrap gap-1.5">
                {names.map((n) => {
                  const on = picked.includes(n);
                  return (
                    <button
                      key={n}
                      type="button"
                      aria-pressed={on}
                      onClick={() => setPicked(on ? picked.filter((x) => x !== n) : [...picked, n])}
                      className={cn("inline-flex h-7 items-center rounded-md border border-border bg-secondary px-2.5 text-xs hover:bg-secondary-hover", on && "border-muted-foreground bg-accent font-semibold")}
                    >
                      {n}
                    </button>
                  );
                })}
              </div>
            </Field>
            <Field label="Paths" hint="Comma-separated globs it may write. Empty means all.">
              <Input mono value={f.paths} onChange={set("paths")} placeholder="src/api/**, docs/**" />
            </Field>
          </div>
        </details>
      </CardContent>
      <CardContent className="flex flex-wrap items-center gap-2">
        <span className="min-w-0 flex-1 text-xs text-muted-foreground">
          Can {KINDS[f.kind].join(", ")} on {scope(repoScope, "*", "repositories")} · {scope(pathScope, "**", "paths")} for {hours(f.ttl)}.
        </span>
        <Button type="button" onClick={onDone}>
          Cancel
        </Button>
        <Button variant="primary" disabled={act.isPending || incomplete}>
          <KeyIcon /> {act.isPending ? "Issuing…" : "Issue token"}
        </Button>
      </CardContent>
      {act.error ? (
        <CardContent>
          <ErrorMessage error={act.error} />
        </CardContent>
      ) : null}
    </form>
  );
}

function IssuedToken({ token, claims, onDone }: { token: string; claims: TokenClaims; onDone: () => void }) {
  const env = `export BEE_URL=${location.origin}\nexport BEE_TOKEN=${token}`;
  return (
    <Alert variant="success" className="mt-0">
      <AlertTitle>
        <CheckCircleIcon /> Token issued to {claims.sub}. Copy it now: it is not shown again.
      </AlertTitle>
      <CopyField value={token} title="Copy token" className="my-3 bg-background" />
      <DescriptionList start className="text-sm">
        <dt>Kind</dt>
        <dd>
          <Badge>{claims.kind}</Badge>
        </dd>
        <dt>Can</dt>
        <dd>{claims.actions.join(", ")}</dd>
        <dt>Repositories</dt>
        <dd className="font-mono">{scope(claims.repos, "*", "repositories")}</dd>
        <dt>Paths</dt>
        <dd className="font-mono">{scope(claims.paths, "**", "paths")}</dd>
        {claims.task && (
          <>
            <dt>Task</dt>
            <dd>{claims.task}</dd>
          </>
        )}
        <dt>Expires</dt>
        <dd>
          {new Date(claims.exp).toLocaleString()} <span className="text-muted-foreground">({timeAgo(claims.exp)})</span>
        </dd>
      </DescriptionList>
      <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
        Use it from a shell <CopyButton value={env} title="Copy shell exports" />
      </div>
      <Pre className="whitespace-pre-wrap break-all bg-background">{env}</Pre>
      <Button className="mt-3" onClick={onDone}>
        Done
      </Button>
    </Alert>
  );
}

function RecentTokens({ onNew }: { onNew: () => void }) {
  const org = useScope();
  const q = useOrgAudit(org, { action: "token." });
  return (
    <Card className="mt-0">
      <CardHeader>
        <HistoryIcon /> Recently issued
        <Button size="sm" variant="primary" className="ml-auto" onClick={onNew}>
          <PlusIcon /> New token
        </Button>
      </CardHeader>
      <Query q={{ ...q, data: q.data?.filter((a) => a.action === "token.issue") }}>
        {(entries) =>
          entries.length ? (
            <Table>
              <TableHeader>
                <tr>
                  <TableHead>Subject</TableHead>
                  <TableHead>Kind</TableHead>
                  <TableHead className="max-md:hidden">Scope</TableHead>
                  <TableHead>Issued</TableHead>
                  <TableHead>Expires</TableHead>
                </tr>
              </TableHeader>
              <TableBody>
                {entries.slice(0, 20).map((a) => {
                  const c = (a.detail ?? {}) as Partial<TokenClaims>;
                  const live = !c.exp || c.exp > Date.now();
                  return (
                    <TableRow key={a.seq}>
                      <TableCell>
                        <Who name={c.sub ?? "?"} size={18} agent={c.kind !== "admin"} />
                        {c.task && <div className="text-xs text-muted-foreground">{c.task}</div>}
                      </TableCell>
                      <TableCell>
                        <Badge>{c.kind}</Badge>
                      </TableCell>
                      <TableCell className="break-words font-mono text-xs max-md:hidden">
                        {scope(c.repos ?? [], "*", "repositories")} · {scope(c.paths ?? [], "**", "paths")}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                        <TimeAgo ms={a.ts} /> by {a.actor}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-xs">
                        {!c.exp ? <span className="text-muted-foreground">never</span> : live ? <TimeAgo ms={c.exp} /> : <Badge>expired</Badge>}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <Empty icon={KeyIcon}>No tokens issued yet.</Empty>
          )
        }
      </Query>
    </Card>
  );
}

/* ---------- job queue ---------- */

export function AdminQueue() {
  const queue = useQueue();
  const [kind, setKind] = useState("");
  return (
    <>
      <Subhead
        title="Job queue"
        actions={
          <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
            <LiveDot tone="green" /> Refreshes every 3s
          </span>
        }
      >

        Background work for the worker processes: <code>bee-runner</code> runs CI, remote sync and imports run automatically on the platform. Jobs run oldest
        first and are retried if a worker stops responding.
      </Subhead>
      <Query q={queue}>{(q) => <QueueView q={q} kind={kind} setKind={setKind} />}</Query>
    </>
  );
}

export function QueueView({ q, kind: picked, setKind }: { q: QueueStats; kind: string; setKind: (k: string) => void }) {
  const count = (k: string, state?: string) => q.jobs.reduce((n, j) => n + (j.kind === k && (!state || j.state === state) ? j.n : 0), 0);
  const kinds = [...new Set(q.jobs.filter((j) => j.n > 0).map((j) => j.kind))];
  const kind = kinds.includes(picked) ? picked : "";
  const claimed = q.claimed.filter((j) => !kind || j.kind === kind);
  const queued = q.queued.filter((j) => !kind || j.kind === kind);
  const total = kind ? count(kind, "queued") : waiting(q);
  const stalled = stalledWorkers(q);
  return (
    <>
      {stalled.map((s) => (
        <Alert key={s.worker} variant="warning" className="mt-0 mb-4">
          <AlertTitle>
            <AlertIcon /> {s.onDemand ? <><code>{workerName(s.worker)}</code> has not picked up its jobs</> : <>No <code>{workerName(s.worker)}</code> is running</>}
          </AlertTitle>
          {s.n} {s.n === 1 ? "job has" : "jobs have"} been waiting for {since(s.oldest)}.{" "}
          {s.onDemand ? (
            <>
              The server starts <code>{workerName(s.worker)}</code> when work is queued, so it should have started by now. Check that its job is deployed and
              its {s.token ? `${s.token} ` : ""}token is valid.
            </>
          ) : s.start ? (
            <>
              Start <code>{s.start}</code> with {s.token ? `a ${s.token}` : "its"} token in <code>BEE_TOKEN</code>.
            </>
          ) : (
            <>The platform runs this automatically; ask your administrator to check the deployment.</>
          )}
        </Alert>
      ))}
      {kinds.length > 1 && (
        <ChipGroup aria-label="Job kind" className="mt-4" value={kind || "all"} onValueChange={(v) => setKind(v === "all" ? "" : v)}>
          <Chip value="all">
            All <Counter>{kinds.reduce((n, k) => n + count(k), 0)}</Counter>
          </Chip>
          {kinds.map((k) => (
            <Chip key={k} value={k}>
              {kindInfo(k).label} <Counter>{count(k)}</Counter>
            </Chip>
          ))}
        </ChipGroup>
      )}

      {claimed.length > 0 && (
        <Card>
          <CardHeader>
            <CpuIcon /> Running <Counter>{claimed.length}</Counter>
          </CardHeader>
          <Table>
            <TableHeader>
              <tr>
                <TableHead>Repository</TableHead>
                <TableHead>Job</TableHead>
                <TableHead>Worker</TableHead>
                <TableHead>Started</TableHead>
              </tr>
            </TableHeader>
            <TableBody>
              {claimed.map((j) => (
                <TableRow key={j.id}>
                  <TableCell>
                    <Link to={rp(j.repo)}>{j.repo}</Link>
                  </TableCell>
                  <TableCell>
                    <JobWork kind={j.kind} repo={j.repo} payload={j.payload} />
                  </TableCell>
                  <TableCell>
                    <Who name={j.claimed_by} size={18} />
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                    <TimeAgo ms={j.claimed_at} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Card>
        <CardHeader>
          <ClockIcon /> Waiting <Counter>{total}</Counter>
          {total > queued.length && <span className="text-xs text-muted-foreground">showing the oldest {queued.length}</span>}
        </CardHeader>
        {queued.length ? (
          <Table>
            <TableHeader>
              <tr>
                <TableHead>Repository</TableHead>
                <TableHead>Job</TableHead>
                <TableHead>Queued</TableHead>
              </tr>
            </TableHeader>
            <TableBody>
              {queued.map((j) => (
                <TableRow key={j.id}>
                  <TableCell>
                    <Link to={rp(j.repo)}>{j.repo}</Link>
                  </TableCell>
                  <TableCell>
                    <JobWork kind={j.kind} repo={j.repo} payload={j.payload} />
                  </TableCell>
                  <TableCell className={cn("whitespace-nowrap text-xs", Date.now() - j.created > STALL_MS ? "text-warning" : "text-muted-foreground")}>
                    <TimeAgo ms={j.created} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <Empty icon={ClockIcon}>Nothing is waiting.</Empty>
        )}
      </Card>
    </>
  );
}

function JobWork({ kind, repo, payload }: { kind: string; repo: string; payload: string }) {
  const p = (safeJson(payload) ?? {}) as { job?: string; commit?: string; change_id?: string; attempt?: number; url?: string; branch?: string };
  if (kind === "remote") {
    return (
      <span className="flex items-center gap-1.5">
        <GitPullRequestIcon size={16} className="text-muted-foreground" /> Pull and push remote
      </span>
    );
  }
  if (kind === "import") {
    return (
      <span className="flex min-w-0 items-center gap-1.5">
        <RepoIcon size={16} className="shrink-0 text-muted-foreground" /> Import
        <span className="truncate text-xs text-muted-foreground" title={p.url}>
          {importSource(p.url)}
          {p.branch && ` @ ${p.branch}`}
        </span>
      </span>
    );
  }
  const change = String(p.change_id ?? "");
  return (
    <span className="flex flex-wrap items-center gap-1.5">
      <Badge>{kind}</Badge>
      {p.job && <b className="font-mono">{p.job}</b>}
      <Sha repo={repo} sha={p.commit} n={7} />
      {change.startsWith("verify:") ? (
        <Badge tone="purple">verification</Badge>
      ) : change ? (
        <Tooltip content="Open change">
          <Link className="font-mono text-xs" to={`${rp(repo)}/changes/${change}`}>
            #{change.slice(-8)}
          </Link>
        </Tooltip>
      ) : null}
      {(p.attempt ?? 1) > 1 && <Badge tone="yellow">attempt {p.attempt}</Badge>}
    </span>
  );
}
